import { tz } from "@date-fns/tz";
import { endOfMonth, set, startOfMonth } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useDRPContext } from "../../context";
import type { IDateRange } from "../../types";
import { useDRMPContext } from "./context";
import type { IYearRange } from "./types";

const initialYearRange = { startDate: new Date().getFullYear(), endDate: new Date().getFullYear() + 1 };
const getYearRange = (range: IDateRange, yearRange: IYearRange = initialYearRange): IYearRange => {
  const nYearRange = { ...yearRange };
  const startYear = range.startDate?.getFullYear();
  const endYear = range.endDate?.getFullYear();

  if (startYear && startYear !== nYearRange.startDate && startYear !== nYearRange.endDate) {
    nYearRange.startDate = startYear;
  }
  if (endYear && endYear !== nYearRange.startDate && endYear !== nYearRange.endDate) {
    if (startYear && endYear !== startYear) {
      nYearRange.startDate = startYear;
    }
    nYearRange.endDate = endYear;
  }
  if (nYearRange.endDate <= nYearRange.startDate) {
    nYearRange.endDate = nYearRange.startDate + 1;
  }
  return nYearRange;
};

export const useDRMP = () => {
  const { range, handleRangeChange, timeZone } = useDRPContext();
  const [yearRange, setYearRange] = useState<IYearRange>(getYearRange(range));
  const [selectRange, setSelectRange] = useState<IDateRange>(range);
  const [lock, setLock] = useState(true);

  const handleMouseEnter = useCallback(
    (date: Date) => {
      setSelectRange((selectRange) => {
        if (selectRange.startDate !== null && !lock && selectRange.startDate.getTime() <= date.getTime()) {
          return { ...selectRange, endDate: date };
        }
        return selectRange;
      });
    },
    [lock]
  );

  const handleMouseLeave = useCallback(() => {
    if (!lock) {
      setSelectRange((selectRange) => {
        return { ...selectRange, endDate: null };
      });
    }
  }, [lock]);

  const handleSelect = useCallback(
    (date: Date, endDateDisabled?: boolean) => {
      setSelectRange((selectRange) => {
        if (selectRange.startDate === null || lock) {
          const range = { startDate: set(startOfMonth(date), { milliseconds: 1 }), endDate: null };
          setLock(false);
          handleRangeChange(range);
          return range;
        }
        if (
          selectRange.startDate !== null &&
          !lock &&
          selectRange.startDate.getTime() <= date.getTime() &&
          !endDateDisabled
        ) {
          const range = {
            startDate: set(startOfMonth(selectRange.startDate), { milliseconds: 1 }),
            endDate: set(endOfMonth(date, { in: tz(timeZone) }), { milliseconds: 998 }),
          };
          setLock(true);
          handleRangeChange(range);
          return range;
        }
        if (selectRange.startDate !== null && !lock && !endDateDisabled) {
          const range = { startDate: set(startOfMonth(date), { milliseconds: 1 }), endDate: null };
          setLock(false);
          handleRangeChange(range);
          return range;
        }
        if (selectRange.startDate !== null && endDateDisabled) {
          const range = { startDate: set(startOfMonth(date), { milliseconds: 1 }), endDate: null };
          setLock(true);
          handleRangeChange(range);
          return range;
        }
        return selectRange;
      });
    },
    [handleRangeChange, lock, timeZone]
  );

  const isSelected = useCallback(
    (date: Date): "start" | "end" | "both" | "false" => {
      if (
        selectRange.startDate !== null &&
        selectRange.startDate.getFullYear() === date.getFullYear() &&
        selectRange.startDate.getMonth() === date.getMonth() &&
        selectRange.endDate !== null &&
        selectRange.endDate.getFullYear() === date.getFullYear() &&
        selectRange.endDate.getMonth() === date.getMonth()
      ) {
        return "both";
      }
      if (
        selectRange.startDate !== null &&
        selectRange.startDate.getFullYear() === date.getFullYear() &&
        selectRange.startDate.getMonth() === date.getMonth()
      ) {
        return "start";
      }
      if (
        selectRange.endDate !== null &&
        selectRange.endDate.getFullYear() === date.getFullYear() &&
        selectRange.endDate.getMonth() === date.getMonth()
      ) {
        return "end";
      }
      return "false";
    },
    [selectRange]
  );

  const isBetween = useCallback(
    (date: Date): boolean => {
      if (selectRange.startDate !== null && selectRange.endDate !== null) {
        const time = date.getTime();
        const start = selectRange.startDate.getTime();
        const end = selectRange.endDate.getTime();
        return (time > start && time < end) || (time > end && time < start);
      }
      return false;
    },
    [selectRange]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setSelectRange((selectedRange) => {
      if (selectedRange !== range) {
        setLock(!(range.startDate && !range.endDate));
      }
      return range;
    });
    setYearRange(getYearRange(range, yearRange));
  }, [range]);

  return {
    yearRange,
    setYearRange,
    selectRange,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
    isSelected,
    isBetween,
  };
};

export const useDRMPYear = (index: keyof IYearRange) => {
  const { yearRange, setYearRange } = useDRMPContext();

  const canPrevious = index === "startDate" ? true : yearRange.endDate - 1 > yearRange.startDate;

  const canNext = index === "startDate" ? yearRange.startDate + 1 < yearRange.endDate : true;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onPrevious = useCallback(() => {
    if (canPrevious) {
      setYearRange((yearRange) => ({ ...yearRange, [index]: yearRange[index] - 1 }));
    }
  }, [index, canPrevious]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onNext = useCallback(() => {
    if (canNext) {
      setYearRange((yearRange) => ({ ...yearRange, [index]: yearRange[index] + 1 }));
    }
  }, [index, canNext]);

  return { year: yearRange[index], onPrevious, onNext, canPrevious, canNext };
};
